import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { MultilingualInput } from '../ui/multilingual-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { TagColorPicker } from '../tags/TagColorPicker'
import { AssetTypeCodesField } from './AssetTypeCodesField'
import { isValidRelationTypeCode, type Cardinality, type RelationTypeFormState } from './relation-type-form'
import { useLocale } from '../../contexts/LocaleContext'

export interface RelationTypeFormFieldsProps {
    org: string
    value: RelationTypeFormState
    onChange: (next: RelationTypeFormState) => void
    /** Creation only — the code is immutable once the type exists. */
    codeEditable: boolean
    /**
     * Lets the host intercept an aggregating flip (the detail page opens an
     * AlertDialog when the type already has relations). When absent, the
     * checkbox writes straight through.
     */
    requestAggregatingChange?: (next: boolean) => void
    disabled?: boolean
}

const CARDINALITIES: Cardinality[] = ['one', 'many']

export function RelationTypeFormFields({
    org,
    value,
    onChange,
    codeEditable,
    requestAggregatingChange,
    disabled,
}: RelationTypeFormFieldsProps) {
    const { t } = useTranslation('types')
    const { locale } = useLocale()

    const patch = useCallback(
        (fields: Partial<RelationTypeFormState>) => onChange({ ...value, ...fields }),
        [onChange, value]
    )

    const codeInvalid = codeEditable && value.code.trim().length > 0 && !isValidRelationTypeCode(value.code)

    // The phrase preview uses the locale currently displayed in the UI so the
    // author sees the wording their own users will read.
    const previewSourceRole = value.sourceRole[locale] || value.sourceRole.en || value.sourceRole.fr || ''
    const previewTargetRole = value.targetRole[locale] || value.targetRole.en || value.targetRole.fr || ''

    const cardinalityExplanation = t(
        `relation_types.cardinality.explain_${value.sourceCardinality}_${value.targetCardinality}`
    )

    return (
        <div className="space-y-6">
            {/* Identity */}
            <div className="space-y-4">
                <div>
                    <Label htmlFor="relation-type-code">{t('relation_types.form.code_label')}</Label>
                    <Input
                        id="relation-type-code"
                        value={value.code}
                        disabled={!codeEditable || disabled}
                        placeholder={t('relation_types.form.code_placeholder')}
                        onChange={e => patch({ code: e.target.value })}
                        data-testid="relation-type-code-input"
                        className="font-mono"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                        {codeEditable
                            ? t('relation_types.form.code_help')
                            : t('relation_types.form.code_immutable')}
                    </p>
                    {codeInvalid && (
                        <p className="mt-1 text-xs text-destructive" data-testid="relation-type-code-error">
                            {t('relation_types.form.code_invalid')}
                        </p>
                    )}
                </div>

                <div data-testid="relation-type-name-field">
                    <Label>{t('relation_types.form.name_label')}</Label>
                    <MultilingualInput
                        id="relation-type-name"
                        value={value.name}
                        onChange={next => patch({ name: next })}
                        required
                        disabled={disabled}
                        data-testid="relation-type-name"
                    />
                </div>

                <div data-testid="relation-type-description-field">
                    <Label>{t('relation_types.form.description_label')}</Label>
                    <MultilingualInput
                        id="relation-type-description"
                        value={value.description}
                        onChange={next => patch({ description: next })}
                        disabled={disabled}
                        data-testid="relation-type-description"
                    />
                </div>
            </div>

            {/* Roles + live phrase preview */}
            <div className="space-y-4 rounded-lg border border-border p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div data-testid="relation-type-source-role-field">
                        <Label>{t('relation_types.form.source_role_label')}</Label>
                        <MultilingualInput
                            id="relation-type-source-role"
                            value={value.sourceRole}
                            onChange={next => patch({ sourceRole: next })}
                            disabled={disabled}
                            data-testid="relation-type-source-role"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('relation_types.form.source_role_help')}
                        </p>
                    </div>
                    <div data-testid="relation-type-target-role-field">
                        <Label>{t('relation_types.form.target_role_label')}</Label>
                        <MultilingualInput
                            id="relation-type-target-role"
                            value={value.targetRole}
                            onChange={next => patch({ targetRole: next })}
                            disabled={disabled}
                            data-testid="relation-type-target-role"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('relation_types.form.target_role_help')}
                        </p>
                    </div>
                </div>

                <div className="rounded-md bg-muted/50 p-3" data-testid="relation-type-phrase-preview">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('relation_types.form.preview_title')}
                    </div>
                    {previewSourceRole || previewTargetRole ? (
                        <ul className="mt-1 space-y-0.5 text-sm">
                            <li data-testid="relation-type-preview-outgoing">
                                {t('relation_types.form.preview_outgoing', { role: previewSourceRole || '…' })}
                            </li>
                            <li data-testid="relation-type-preview-incoming">
                                {t('relation_types.form.preview_incoming', { role: previewTargetRole || '…' })}
                            </li>
                        </ul>
                    ) : (
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('relation_types.form.preview_empty')}
                        </p>
                    )}
                </div>
            </div>

            {/* Appearance */}
            <div className="space-y-4 rounded-lg border border-border p-4">
                <div className="text-sm font-semibold text-foreground">
                    {t('relation_types.form.appearance_title')}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <Label htmlFor="relation-type-emoji">{t('relation_types.form.emoji_label')}</Label>
                        <Input
                            id="relation-type-emoji"
                            value={value.emoji}
                            disabled={disabled}
                            maxLength={8}
                            placeholder={t('relation_types.form.emoji_placeholder')}
                            onChange={e => patch({ emoji: e.target.value })}
                            data-testid="relation-type-emoji-input"
                            className="w-24 text-center text-lg"
                        />
                    </div>
                    <div>
                        <Label htmlFor="relation-type-color">{t('relation_types.form.color_label')}</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                id="relation-type-color"
                                value={value.color}
                                disabled={disabled}
                                placeholder={t('relation_types.form.color_placeholder')}
                                onChange={e => patch({ color: e.target.value })}
                                data-testid="relation-type-color-input"
                                className="w-32 font-mono"
                            />
                            {value.color && !disabled && (
                                <button
                                    type="button"
                                    className="text-xs text-muted-foreground underline"
                                    onClick={() => patch({ color: '' })}
                                    data-testid="relation-type-color-clear"
                                >
                                    {t('relation_types.form.color_clear')}
                                </button>
                            )}
                        </div>
                        {/* The picker is every-swatch-is-a-button: there is no
                            read-only rendering of it, and the hex input above
                            already shows the stored colour. */}
                        {!disabled && (
                            <div className="mt-2">
                                <TagColorPicker value={value.color} onChange={color => patch({ color })} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Allowed asset types */}
            <div className="space-y-4 rounded-lg border border-border p-4">
                <div className="text-sm font-semibold text-foreground">
                    {t('relation_types.form.constraints_title')}
                </div>
                <AssetTypeCodesField
                    org={org}
                    label={t('relation_types.form.source_types_label')}
                    value={value.sourceTypeCodes}
                    onChange={codes => patch({ sourceTypeCodes: codes })}
                    disabled={disabled}
                    testIdPrefix="relation-type-source-types"
                />
                <AssetTypeCodesField
                    org={org}
                    label={t('relation_types.form.target_types_label')}
                    value={value.destTypeCodes}
                    onChange={codes => patch({ destTypeCodes: codes })}
                    disabled={disabled}
                    testIdPrefix="relation-type-target-types"
                />
            </div>

            {/* Behavior */}
            <div className="space-y-4 rounded-lg border border-border p-4">
                <div className="text-sm font-semibold text-foreground">
                    {t('relation_types.form.behavior_title')}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <Label>{t('relation_types.form.source_cardinality_label')}</Label>
                        <Select
                            value={value.sourceCardinality}
                            disabled={disabled}
                            onValueChange={v => patch({ sourceCardinality: v as Cardinality })}
                        >
                            <SelectTrigger data-testid="relation-type-source-cardinality">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CARDINALITIES.map(c => (
                                    <SelectItem key={c} value={c} data-testid={`relation-type-source-cardinality-${c}`}>
                                        {t(`relation_types.cardinality.${c}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>{t('relation_types.form.target_cardinality_label')}</Label>
                        <Select
                            value={value.targetCardinality}
                            disabled={disabled}
                            onValueChange={v => patch({ targetCardinality: v as Cardinality })}
                        >
                            <SelectTrigger data-testid="relation-type-target-cardinality">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CARDINALITIES.map(c => (
                                    <SelectItem key={c} value={c} data-testid={`relation-type-target-cardinality-${c}`}>
                                        {t(`relation_types.cardinality.${c}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="relation-type-cardinality-explanation">
                    {cardinalityExplanation}
                </p>

                <div className="flex items-start gap-3">
                    <Checkbox
                        id="relation-type-aggregating"
                        checked={value.aggregating}
                        disabled={disabled}
                        onCheckedChange={checked => {
                            const next = checked === true
                            if (requestAggregatingChange) requestAggregatingChange(next)
                            else patch({ aggregating: next })
                        }}
                        data-testid="relation-type-aggregating-toggle"
                    />
                    <div className="min-w-0">
                        <Label htmlFor="relation-type-aggregating">
                            {t('relation_types.form.aggregating_label')}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            {t('relation_types.form.aggregating_help')}
                        </p>
                        <p
                            className="mt-1 text-xs text-warning"
                            data-testid="relation-type-aggregating-warning"
                        >
                            {t('relation_types.form.aggregating_warning')}
                        </p>
                    </div>
                </div>

                <div>
                    <Label htmlFor="relation-type-ordering">{t('relation_types.form.ordering_label')}</Label>
                    <Input
                        id="relation-type-ordering"
                        type="number"
                        value={value.ordering}
                        disabled={disabled}
                        onChange={e => patch({ ordering: e.target.value })}
                        data-testid="relation-type-ordering-input"
                        className="w-32"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t('relation_types.form.ordering_help')}
                    </p>
                </div>
            </div>
        </div>
    )
}
